import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Share2, ArrowLeft, Loader2, Play } from 'lucide-react';
import { getInitials, formatNumber } from '../lib/utils';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";
const SITE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PublicPostPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/public/posts/${postId}`);
      setPost(response.data);
    } catch (err) {
      console.error('Failed to fetch post:', err);
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const getPostImage = () => {
    if (post?.media && post.media.length > 0) {
      const firstMedia = post.media[0];
      if (typeof firstMedia === 'string' && !firstMedia.startsWith('data:')) {
        return firstMedia;
      }
    }
    return LOGO_URL;
  };

  const getDescription = () => {
    return post?.content?.substring(0, 200) || 'View this post on FityH';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="font-semibold">Post</span>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-xl font-semibold mb-2">Post not found</p>
          <p className="text-muted-foreground mb-4">This post may have been deleted or doesn't exist.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  const user = post.user || {};
  const title = `${user.display_name || user.username || 'Someone'} on FityH`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={getDescription()} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${SITE_URL}/p/${post.id}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={getDescription()} />
        <meta property="og:image" content={getPostImage()} />
        <meta property="og:site_name" content="FityH" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${SITE_URL}/p/${post.id}`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={getDescription()} />
        <meta property="twitter:image" content={getPostImage()} />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <span className="font-semibold">Post</span>
            </div>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FityH" className="w-8 h-8" />
              <span className="text-lg font-bold text-primary">FityH</span>
            </div>
          </div>
        </header>

        {/* Post Content */}
        <div className="max-w-2xl mx-auto px-4 py-6">
          <article className="bg-card rounded-2xl border border-border/50 overflow-hidden">
            {/* Author */}
            <div className="p-4 flex items-center justify-between">
              <Link 
                to={`/@${user.username}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(user.display_name || user.username)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{user.display_name || user.username}</p>
                  <p className="text-sm text-muted-foreground">@{user.username}</p>
                </div>
              </Link>
              <Button 
                size="sm" 
                className="rounded-full"
                onClick={() => navigate('/register')}
              >
                Follow
              </Button>
            </div>

            {/* Media */}
            {post.media && post.media.length > 0 && (
              <div className="bg-secondary">
                {post.media_type === 'video' ? (
                  <div className="aspect-video flex items-center justify-center bg-black">
                    <Play className="w-16 h-16 text-white/80" />
                    <p className="absolute bottom-4 left-4 text-white text-sm">Sign up to watch videos</p>
                  </div>
                ) : (
                  <img 
                    src={post.media[0]} 
                    alt="" 
                    className="w-full max-h-[500px] object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-4">
              <p className="text-lg whitespace-pre-wrap mb-4">{post.content}</p>
              
              {/* Hashtags */}
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.hashtags.map((tag, i) => (
                    <span key={i} className="text-primary hover:underline cursor-pointer">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Time */}
              <p className="text-sm text-muted-foreground mb-4">
                {formatTimeAgo(post.created_at)}
              </p>

              {/* Stats */}
              <div className="flex items-center gap-6 py-4 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-muted-foreground" />
                  <span className="font-semibold">{formatNumber(post.likes_count)}</span>
                  <span className="text-muted-foreground">likes</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-muted-foreground" />
                  <span className="font-semibold">{formatNumber(post.comments_count)}</span>
                  <span className="text-muted-foreground">comments</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-border/50">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={() => navigate('/register')}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Like
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={() => navigate('/register')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Comment
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={() => {
                    navigator.clipboard.writeText(`${SITE_URL}/p/${post.id}`);
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </article>

          {/* Comments Preview */}
          {post.recent_comments && post.recent_comments.length > 0 && (
            <div className="mt-4 bg-card rounded-2xl border border-border/50 p-4">
              <h3 className="font-semibold mb-4">Comments</h3>
              {post.recent_comments.slice(0, 3).map((comment, i) => (
                <div key={i} className="flex gap-3 mb-3">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {getInitials(comment.username)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm">
                      <span className="font-semibold">{comment.username}</span>{' '}
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))}
              <Button 
                variant="ghost" 
                className="w-full mt-2"
                onClick={() => navigate('/register')}
              >
                View all comments
              </Button>
            </div>
          )}

          {/* Join CTA */}
          <div className="text-center py-8 mt-4">
            <p className="text-lg font-semibold mb-2">Join the conversation</p>
            <p className="text-muted-foreground mb-4">
              Sign up to like, comment, and connect with creators on FityH
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/register')} className="rounded-full px-8">
                Sign Up Free
              </Button>
              <Button variant="outline" onClick={() => navigate('/login')} className="rounded-full px-8">
                Log In
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-border/50 py-6">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <Link to="/" className="flex items-center justify-center gap-2 mb-4">
              <img src={LOGO_URL} alt="FityH" className="w-8 h-8" />
              <span className="text-lg font-bold text-primary">FityH</span>
            </Link>
            <p className="text-sm text-muted-foreground">
              Connect. Share. Create. Earn.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
